import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Rocket, Mail, ArrowRight, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/coming-soon")({
  component: ComingSoonPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Coming soon — Spayzo" },
      {
        name: "description",
        content:
          "Something new is launching on Spayzo. Join the waitlist to be the first to know.",
      },
    ],
  }),
});

const LAUNCH_DATE = new Date(Date.now() + 1000 * 60 * 60 * 24 * 14); // 14 days

function useCountdown(target: Date) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function ComingSoonPage() {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    toast.success("You're on the list! We'll notify you at launch.");
    setEmail("");
  };

  const cells = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Minutes", value: minutes },
    { label: "Seconds", value: seconds },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_hsl(var(--primary)/0.14),_transparent_60%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-64 bg-[radial-gradient(ellipse_at_bottom,_hsl(var(--primary)/0.10),_transparent_70%)]" />

      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
          <Bell className="h-3.5 w-3.5 text-primary" />
          New experience launching soon
        </div>

        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
          <Rocket className="h-9 w-9" />
        </div>

        <h1 className="text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">
          Something great is on the way
        </h1>
        <p className="mt-4 max-w-xl text-pretty text-base text-muted-foreground sm:text-lg">
          We're putting the finishing touches on a new way to discover and book
          spaces by the hour. Join the waitlist and we'll let you know the moment
          it goes live.
        </p>

        <div className="mt-10 grid w-full max-w-lg grid-cols-4 gap-3">
          {cells.map((c) => (
            <div
              key={c.label}
              className="rounded-xl border bg-card p-3 shadow-sm sm:p-4"
            >
              <div className="text-2xl font-semibold tabular-nums tracking-tight text-foreground sm:text-4xl">
                {String(c.value).padStart(2, "0")}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">
                {c.label}
              </div>
            </div>
          ))}
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-10 flex w-full max-w-md flex-col gap-2 sm:flex-row"
        >
          <div className="relative flex-1">
            <Mail className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="email"
              required
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 ps-9"
            />
          </div>
          <Button type="submit" className="h-11">
            Notify me <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

      </div>
    </div>
  );
}
